import React, { useEffect, useState } from 'react';
import './dotsAnimation.css';
import { Box, Typography } from '@mui/material';
import { I_BoxDataValue } from '../Log/graph.util';

export interface TopologyFlowProps {
  orientation: string;
  position?: string;
  data?: I_BoxDataValue;
  amount?: number;
  showValues: boolean;
}

function getRandomStyle() {
  const animationDelay = `${Math.random() * 2}s`; // Random delay between 0 and 2 seconds
  const top = `${Math.random() * 100}%`; // Random top position within the container
  return { animationDelay, top };
}

function getRandomStyleVertical() {
  const animationDelay = `${Math.random() * 2}s`; // Random delay between 0 and 2 seconds
  const left = `${Math.random() * 100}%`; // Random top position within the container
  return { animationDelay, left };
}

function formatPower(value) {
  if (isNaN(value)) {
    return 'Invalid';
  }

  value = parseFloat(value);

  const prefixes = ['', 'k', 'M', 'G', 'T'];
  let magnitude = 0;
  let negative = false;

  if (value < 0) {
    value = -value;
    negative = true;
  }
  while (value >= 1000) {
    value /= 1000;
    magnitude++;
  }

  const roundedValue = value.toFixed(1);

  //Filter all values less than 100 Watts
  if (magnitude === 0 && value < 100) {
    return 0;
  }

  return negative === false
    ? `${roundedValue} ${prefixes[magnitude]}`
    : `-${roundedValue} ${prefixes[magnitude]}`;
}

function TopologyFlow(props: TopologyFlowProps) {
  const [dotStyles, setDotStyle] = useState<
    { animationDelay: string; top: string }[]
  >([]);

  const [dotStylesVertical, setDotStyleVertical] = useState<
    { animationDelay: string; left: string }[]
  >([]);

  const numOfDots = 200;
  const minNumberOfDots = 50;
  const minHeight = 2;
  const maxHeight = 70;
  const minWidth = 2;
  const maxWidth = 68;
  const maxWidthMobile = 50;
  const isMobile = window.innerWidth <= 1490;

  const desiredNumOfDots =
    numOfDots * props.amount < minNumberOfDots
      ? minNumberOfDots
      : numOfDots * props.amount > numOfDots
      ? numOfDots
      : numOfDots * props.amount;

  useEffect(() => {
    setDotStyle(
      Array.from({ length: desiredNumOfDots }, () => getRandomStyle())
    );

    setDotStyleVertical(
      Array.from({ length: desiredNumOfDots }, () => getRandomStyleVertical())
    );
  }, [desiredNumOfDots]);

  return (
    <>
      {props.data && props.orientation === 'horizontal' && (
        <Box
          sx={{
            position: 'absolute',
            marginLeft: isMobile ? '142px' : '170px',
            marginTop: '2px',
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1
          }}
        >
          {props.showValues && props.data.value != 0 && (
            <Typography
              sx={{
                marginTop: '1px',
                color: 'black',
                bgcolor: 'background.paper',
                borderRadius: 5,
                fontSize: '0.75rem',
                zIndex: 1
              }}
            >
              {formatPower(props.data.value) === 0
                ? null
                : formatPower(props.data.value)}
              {formatPower(props.data.value) === 0 ? null : props.data.unit}
            </Typography>
          )}
        </Box>
      )}
      <div
        className={`${
          props.orientation === 'vertical' ? 'line' : 'horizontalLine'
        } ${isMobile ? 'isMobile' : ''}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position:
            props.orientation === 'horizontal' ? 'absolute' : 'relative',
          height:
            props.orientation === 'horizontal'
              ? `${Math.min(
                  Math.max(props.amount * maxHeight, minHeight),
                  maxHeight
                )}px`
              : `${maxHeight}px`,
          width:
            props.orientation === 'vertical'
              ? `${Math.min(
                  Math.max(props.amount * maxWidth, minWidth),
                  maxWidth
                )}px`
              : isMobile
              ? `${maxWidthMobile}px`
              : `${maxWidth}px`
        }}
      >
        {props.orientation === 'horizontal' &&
          props.data &&
          props.data.value != 0 && (
            <>
              <div className="container">
                {dotStyles.map((style, index) => (
                  <div
                    className={
                      (props.data.value as number) >= 0 ? 'dotRight' : 'dotLeft'
                    }
                    key={index}
                    style={{
                      animationDelay: style.animationDelay,
                      top: style.top
                    }}
                  ></div>
                ))}
              </div>
            </>
          )}

        {props.orientation === 'vertical' &&
          props.data &&
          props.data.value != 0 && (
            <div className="dot-container">
              {dotStylesVertical.map((style, index) => (
                <div
                  className="verticalDotDown"
                  key={index}
                  style={{
                    animationDelay: style.animationDelay,
                    left: style.left
                  }}
                ></div>
              ))}
            </div>
          )}
      </div>
    </>
  );
}

export default TopologyFlow;
